import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from '@phosphor-icons/react';
import { useLenis } from '../../lib/SmoothScroll';
import { scrollToId } from '../../lib/scroll';

type Variant = 'primary' | 'secondary' | 'dusk' | 'dusk-secondary';
type Size = 'md' | 'lg';

type ButtonProps = {
	children: ReactNode;
	variant?: Variant;
	size?: Size;
	/** In-page anchor ("#contact"), internal route ("/globe") or external URL. */
	href?: string;
	/** Trailing arrow: "go" (in-page / internal) or "out" (external). */
	arrow?: 'go' | 'out';
	type?: 'button' | 'submit';
	disabled?: boolean;
	onClick?: () => void;
	className?: string;
};

/**
 * Mechanics (twin arrows swapping in their disc, press, focus) live in src/styles/brand.css (.btn);
 * this file only sets each variant's palette as custom properties, light and dark.
 * The label speaks in the display face, like the headlines.
 */
const base = 'btn font-display font-bold tracking-[-0.005em]';

/*
 * Each variant: resting colours, the disc colour under the arrow (--btn-fill) and the
 * arrow colour that reads on it. Contrast: graphite on solar 8.9:1, mineral on forest
 * 8.8:1, solar on forest-deep 9.6:1, graphite on mineral 13:1.
 */
const DUSK =
	'[--btn-bg:#E8C96A] [--btn-ink:#252B27] [--btn-line:transparent] [--btn-fill:#16291F] [--btn-arrow:#E8C96A] [--btn-ring:#E8C96A] [--btn-shadow:inset_0_1px_0_rgba(247,247,240,0.4)]';
const DUSK_SECONDARY =
	'[--btn-bg:transparent] [--btn-ink:#F7F7F0] [--btn-line:rgba(247,247,240,0.25)] [--btn-fill:#E8C96A] [--btn-arrow:#252B27] [--btn-ring:#E8C96A] [--btn-shadow:0_0_#0000]';

const variants: Record<Variant, string> = {
	// Forest pill, a solar disc under the arrow.
	// Dark theme: the dusk pair (solar pill, graphite label).
	primary:
		'[--btn-bg:#294D3B] [--btn-ink:#F7F7F0] [--btn-line:transparent] [--btn-fill:#E8C96A] [--btn-arrow:#252B27] [--btn-ring:#294D3B] [--btn-shadow:0_12px_32px_-14px_rgba(41,77,59,0.45),inset_0_1px_0_rgba(247,247,240,0.14)] ' +
		'dark:[--btn-bg:#E8C96A] dark:[--btn-ink:#252B27] dark:[--btn-fill:#16291F] dark:[--btn-arrow:#E8C96A] dark:[--btn-ring:#E8C96A] dark:[--btn-shadow:inset_0_1px_0_rgba(247,247,240,0.4)]',
	// Hairline outline, a forest disc under the arrow.
	secondary:
		'[--btn-bg:transparent] [--btn-ink:#252B27] [--btn-line:rgba(37,43,39,0.2)] [--btn-fill:#294D3B] [--btn-arrow:#F7F7F0] [--btn-ring:#294D3B] [--btn-shadow:0_0_#0000] ' +
		'dark:[--btn-ink:#F7F7F0] dark:[--btn-line:rgba(247,247,240,0.25)] dark:[--btn-fill:#E8C96A] dark:[--btn-arrow:#252B27] dark:[--btn-ring:#E8C96A]',
	// Forest Night chapter (Vision, Contact): theme independent.
	dusk: DUSK,
	'dusk-secondary': DUSK_SECONDARY,
};

const sizes: Record<Size, string> = {
	md: '[--btn-h:3rem] px-6 text-[15px]',
	lg: '[--btn-h:3.5rem] px-8 text-base',
};

export function Button({
	children,
	variant = 'primary',
	size = 'md',
	href,
	arrow,
	type = 'button',
	disabled,
	onClick,
	className = '',
}: ButtonProps) {
	const lenis = useLenis();
	const classes = `${base} ${variants[variant]} ${sizes[size]} ${className}`;
	const Icon = arrow === 'out' ? ArrowUpRight : ArrowRight;
	const iconSize = size === 'lg' ? 19 : 17;
	const inner = (
		<>
			<span aria-hidden className="btn-fill" />
			<span className="btn-label">{children}</span>
			{arrow && (
				<span aria-hidden className="btn-arrow">
					<Icon size={iconSize} weight="bold" />
					<Icon size={iconSize} weight="bold" />
				</span>
			)}
		</>
	);

	if (href?.startsWith('#')) {
		const onAnchor = (e: MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			scrollToId(lenis, href.slice(1));
			onClick?.();
		};
		return (
			<a href={href} onClick={onAnchor} className={classes} data-arrow={arrow}>
				{inner}
			</a>
		);
	}

	if (href?.startsWith('/')) {
		return (
			<Link to={href} onClick={onClick} className={classes} data-arrow={arrow}>
				{inner}
			</Link>
		);
	}

	if (href) {
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				onClick={onClick}
				className={classes}
				data-arrow={arrow}
			>
				{inner}
			</a>
		);
	}

	return (
		<button type={type} disabled={disabled} onClick={onClick} className={classes} data-arrow={arrow}>
			{inner}
		</button>
	);
}
